import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Cinematic reveal of the winner screen.
 *
 *   [badge]  →  title  →  image  →  [profit]  →  fmv  →  cta
 *
 * `badge` opens the show: glow bloom rises and the outline letters
 * (GRAIL / CHASE / …) widen out from a single vertical sliver in
 * the centre. The title typewriter only starts once the backdrop
 * has settled, so the headline reads against an atmospheric, not
 * empty, frame.
 *
 * `title` waits for the typewriter's own completion callback —
 * long card names ("2024 PANINI PRIZM GOLD CHOICE JAMES COOK 29
 * PSA 9") get to finish typing before the next slot lifts in.
 * Every later step auto-advances on a fixed delay so the
 * sequence reads like one choreographed beat instead of a
 * blur of pop-ins.
 *
 * `badge` is dropped from the order when the item has no badge —
 * no point sitting on an empty pause waiting for a backdrop
 * that won't render.
 *
 * `profit` is dropped entirely when there's no positive delta
 * (the fallback FMV-only block plays in `fmv` instead).
 */
export type RevealStage =
  | 'title'
  | 'image'
  | 'badge'
  | 'profit'
  | 'fmv'
  | 'cta';

/** How long the named stage stays on stage before the next slot
 *  is unveiled. Beats with an inner odometer (profit / fmv) play
 *  a three-part choreography:
 *
 *    600 ms  — fade + lift entrance lands
 *    ROLL_START_DELAY_MS — paused-on-zero so the eye reads "0.00"
 *    ~900–1100 ms — odometer rolls (durationMs + per-digit stagger)
 *    ~250 ms — settled breath before handing off to the next beat
 *
 *  Numbers below bake those in. `cta` is the last stage, so its
 *  hold isn't used. */
const STAGE_HOLD_MS: Record<RevealStage, number> = {
  // Bloom (1.4 s) + outline letters widen from centre (≈1.0 s) +
  // a short settle before the title typewriter kicks in.
  badge: 1500,
  title: 0,
  image: 650,
  profit: 2400,
  fmv: 2450,
  cta: 0,
};

/** How long after a row becomes visible we hold its odometer
 *  parked on zeros before rolling. Slightly longer than the row's
 *  600 ms entrance so the eye registers the "$0.00" frame before
 *  the slot reels start spinning. */
export const ROLL_START_DELAY_MS = 700;

interface UseRevealSequenceOptions {
  hasProfit: boolean;
  /** Whether the item carries a badge (Grail / Chase / Rare / …).
   *  When false the `badge` beat is dropped from the order — no
   *  point holding a 1.7 s pause for a backdrop that won't render. */
  hasBadge: boolean;
  /** Returning view — skip the cascade entirely and start on the
   *  LAST stage so `visible()` reports true for every beat from the
   *  first render. No cascade timers ever fire. Default false. */
  instant?: boolean;
}

export function useRevealSequence({
  hasProfit,
  hasBadge,
  instant = false,
}: UseRevealSequenceOptions) {
  const order = useMemo<RevealStage[]>(() => {
    const stages: RevealStage[] = [];
    if (hasBadge) stages.push('badge');
    stages.push('title', 'image');
    if (hasProfit) stages.push('profit');
    stages.push('fmv', 'cta');
    return stages;
  }, [hasProfit, hasBadge]);

  // Start on whichever stage `order` opens with (badge if present,
  // else title). Lazy init: `hasBadge` / `hasProfit` are stable for
  // the lifetime of WinnerStep, so we don't need to react to later
  // changes. When `instant`, jump straight to the LAST stage so every
  // beat is `visible()` from the first render and no timers run.
  const [stage, setStage] = useState<RevealStage>(() =>
    instant ? order[order.length - 1]! : order[0]!,
  );

  const advance = useCallback(() => {
    setStage((current) => {
      const i = order.indexOf(current);
      return i >= 0 && i < order.length - 1 ? order[i + 1]! : current;
    });
  }, [order]);

  useEffect(() => {
    if (stage === 'title') return;
    if (stage === order[order.length - 1]) return;
    const t = window.setTimeout(advance, STAGE_HOLD_MS[stage]);
    return () => window.clearTimeout(t);
  }, [stage, order, advance]);

  const titleDone = useCallback(() => {
    setStage((s) => {
      if (s !== 'title') return s;
      // `title` isn't necessarily order[0] anymore — when a badge
      // beat plays first, title sits at index 1. Find the slot
      // that comes AFTER 'title' explicitly.
      const i = order.indexOf('title');
      return i >= 0 && i < order.length - 1 ? order[i + 1]! : s;
    });
  }, [order]);

  const visible = useCallback(
    (target: RevealStage) => {
      const targetIdx = order.indexOf(target);
      if (targetIdx === -1) return false;
      return order.indexOf(stage) >= targetIdx;
    },
    [stage, order],
  );

  return { titleDone, visible };
}
